import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast, Toaster } from 'sonner';
import { api } from '@/lib/api';
import { Phone, Mail, MapPin, ArrowLeft, User, CheckCircle2 } from 'lucide-react';

export default function ContactPage() {
  const nav = useNavigate();
  const [f, setF] = useState({ name: '', email: '', subject: '', body: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/public/contact', f);
      setDone(true);
      toast.success('Message sent. Our team will respond shortly.');
    } catch {
      toast.error('Could not send message. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-16 lg:py-20" data-testid="contact-page">
      <Toaster />
      <button
        onClick={() => nav('/')}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
        data-testid="contact-back-link"
      >
        <ArrowLeft size={14} /> Back to homepage
      </button>

      <div className="mt-4 max-w-2xl">
        <div className="text-xs uppercase tracking-widest text-primary font-semibold">Contact</div>
        <h1 className="font-display text-3xl md:text-4xl lg:text-[42px] font-semibold mt-3 leading-tight">
          Speak with the Aakash Drishti team.
        </h1>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          For product demonstrations, partnership enquiries, procurement or pilot conversations, reach out to the Any Tech Ventures team.
        </p>
      </div>

      <div className="mt-10 grid lg:grid-cols-5 gap-6">
        <Card className="p-6 border-border lg:col-span-2" data-testid="contact-info-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Aakash Drishti · Sales & Partnerships</div>
          <div className="mt-4 space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-md bg-primary/10 text-primary p-2"><User size={16} /></div>
              <div>
                <div className="font-semibold" data-testid="contact-person-name">Abhishek Tyagi</div>
                <div className="text-muted-foreground text-xs">Business Development · Any Tech Ventures Pvt. Ltd.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="rounded-md bg-primary/10 text-primary p-2"><Phone size={16} /></div>
              <div>
                <div className="font-mono text-[14px]" data-testid="contact-phone">+91-9870135066</div>
                <div className="text-muted-foreground text-xs">Direct line · IST business hours</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="rounded-md bg-primary/10 text-primary p-2"><Mail size={16} /></div>
              <div>
                <a href="mailto:hello@anytechventures.com" className="text-[14px] hover:text-primary underline underline-offset-2" data-testid="contact-email-general">hello@anytechventures.com</a>
                <div className="text-muted-foreground text-xs">General enquiries</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="rounded-md bg-primary/10 text-primary p-2"><MapPin size={16} /></div>
              <div>
                <div className="text-[14px]">Any Tech Ventures Pvt. Ltd.</div>
                <div className="text-muted-foreground text-xs">India</div>
                <a href="https://anytechventures.com/" target="_blank" rel="noreferrer" className="mt-1 inline-block text-[12px] text-primary hover:underline" data-testid="contact-atv-link">
                  anytechventures.com
                </a>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-border text-[11px] text-muted-foreground leading-relaxed">
            For an executive demonstration of the platform,{' '}
            <button className="underline hover:text-primary" onClick={() => nav('/request-demo')}>
              please use the dedicated request form
            </button>
            .
          </div>
        </Card>

        {done ? (
          <Card className="p-10 border-border text-center lg:col-span-3" data-testid="contact-success">
            <CheckCircle2 size={40} className="text-primary mx-auto" />
            <div className="mt-4 font-display text-xl font-semibold">Message received.</div>
            <div className="mt-2 text-muted-foreground">Thank you. Our team will respond within one business day.</div>
            <div className="mt-6">
              <Button variant="outline" onClick={() => nav('/')}>Return to homepage</Button>
            </div>
          </Card>
        ) : (
          <Card className="p-6 lg:p-8 border-border lg:col-span-3">
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" data-testid="contact-form">
              <div>
                <Label htmlFor="contact-name">Name <span className="text-red-500">*</span></Label>
                <Input id="contact-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name" />
              </div>
              <div>
                <Label htmlFor="contact-email">Email <span className="text-red-500">*</span></Label>
                <Input id="contact-email" required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="contact-subject">Subject</Label>
                <Input id="contact-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="e.g., Enquiry about hydropower pilot" data-testid="contact-subject" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="contact-body">Message <span className="text-red-500">*</span></Label>
                <Textarea id="contact-body" rows={6} required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Please share the context of your enquiry and preferred means of response." data-testid="contact-body" />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" disabled={busy} data-testid="contact-submit">
                  {busy ? 'Sending…' : 'Send message'}
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
